<template>
  <div class="dealer-bike-detail">
    <v-card elevation="5" class="mx-0 mx-md-12 px-0 px-md-6 py-6" v-if="dealerBikeSelected">
      <v-toolbar elevation="0">
        <v-toolbar-title class="title-detail-card">
          {{ dealerBikeSelected.vehicle.model }}
        </v-toolbar-title>
      </v-toolbar>

      <v-expansion-panels v-model="panels" multiple flat>
        <expansion-panel
          uid="expansion-panel-dealer-bike-detail-vehicle-data"
          :title="$t('dealer-bike.detail.vehicle-data-title')"
          :readonly="false">
          <template v-slot:panel-content v-if="dealerBikeSelected.vehicle">
            <m-form-row :label="$t('dealer-bike.detail.model')">
              {{ dealerBikeSelected.vehicle.model }}
            </m-form-row>

            <m-form-row :label="$t('dealer-bike.detail.color')">
              {{ dealerBikeSelected.vehicle.color }}
            </m-form-row>

            <m-form-row :label="$t('dealer-bike.detail.vin')">
              {{ dealerBikeSelected.vehicle.vin }}
            </m-form-row>

            <m-form-row :label="$t('dealer-bike.detail.registration-number')">
              {{ dealerBikeSelected.vehicle.registrationNumber }}
            </m-form-row>

            <m-form-row :label="$t('dealer-bike.detail.date-plate')">
              {{ getDataFormatted(dealerBikeSelected.vehicle.registrationDate) }}
            </m-form-row>

            <m-form-row :label="$t('dealer-bike.detail.license')">
              {{ dealerBikeSelected.vehicle.license }}
            </m-form-row>

            <m-form-row :label="$t('dealer-bike.detail.type')">
              {{ $t(`dealer-bike.list.type.${dealerBikeSelected.usageType}`) }}
            </m-form-row>

            <m-form-row :label="$t('dealer-bike.detail.state')">
              <div class="text-item">
                <v-icon size="8" :color="dealerBikeSelected.statusColor">fa-solid fa-circle</v-icon>
                <span>&nbsp;{{ $t(`dealer-bike.list.status.${dealerBikeSelected.status}`) }}</span>
              </div>
            </m-form-row>
          </template>
        </expansion-panel>

        <expansion-panel
          uid="expansion-panel-dealer-bike-detail-device"
          :title="$t('dealer-bike.detail.device-data-title')"
          :readonly="false"
          :hideDivider="false">
          <template v-slot:panel-content v-if="dealerBikeSelected.device">
            <m-form-row :label="$t('dealer-bike.detail.device-code')">
              <imei :value="dealerBikeSelected.device.imei" />
            </m-form-row>

            <m-form-row :label="$t('dealer-bike.detail.total-km')">
              {{ $t('dealer-bike.detail.km-unit', {kms: Math.ceil(vehicleTotalKm)}) }}
            </m-form-row>
          </template>
        </expansion-panel>

        <expansion-panel
          :title="$t('dealer-bike.detail.fleet-location-title')"
          :readonly="false"
          :hideDivider="false"
          data-panel="fleet-location"
          v-if="permissions.fleetManagement.canSee">
          <template v-slot:panel-content>
            <m-form-row v-if="!isAvailable()">
              <v-alert
                class="justify-center font-weight-bold"
                type="warning"
                icon="fa-solid fa-triangle-exclamation"
                text>
                {{ computedErrorMessage }}
              </v-alert>
            </m-form-row>
            <m-form-row class="p-0 google-map-wrapper" v-else-if="!dealerBikeLoading && !loadingWebsocket">
              <GmapMap
                v-if="currentMapCenter !== null && hasActiveSubscription()"
                :center="currentMapCenter"
                :zoom="15"
                map-type-id="roadmap"
                style="width: 100%; height: 300px"
                :options="{
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                  zoomControl: false,
                  disableDefaultUi: false,
                  clickableIcons: false,
                }"
                ref="mapRef">
                <GmapInfoWindow
                  :opened="true"
                  :position="currentMapCenter"
                  :options="{pixelOffset: {width: 0, height: -18}}">
                  <div class="fleet-info-background"></div>
                  <div class="motorbike-circle" @click="centerPosition(currentMapCenter)"></div>
                  <div class="motorbike-icon-wrapper" @click="centerPosition(currentMapCenter)">
                    <v-icon dense color="white" size="28">fak fa-light fa-m-motorcycle-touring</v-icon>
                  </div>

                  <div class="fleet-info-wrapper" @click="centerPosition(currentMapCenter)">
                    <div class="fleet-info">
                      <label-tooltip :show="true" :bottom="true">
                        <v-icon
                          dense
                          color="primary"
                          class="fa-light fa-circle-info fleet-info-icon"
                          style="line-height: 1.5" />
                        <template #tooltip>{{ $t('dealer-bike.detail.fleet-location.label-status') }}</template>
                      </label-tooltip>
                      <span>{{ getDeviceStatus(currentPositionStatus) }}</span>
                    </div>

                    <div class="fleet-info">
                      <label-tooltip :show="true" :bottom="true">
                        <v-icon
                          dense
                          color="primary"
                          class="fa-light fa-location-crosshairs fleet-info-icon"
                          style="line-height: 1.5" />
                        <template #tooltip>{{ $t('dealer-bike.detail.fleet-location.label-last-update') }}</template>
                      </label-tooltip>
                      <span>{{ currentPositionDate }}</span>
                    </div>

                    <div class="fleet-info">
                      <label-tooltip :show="true" :bottom="true">
                        <v-icon
                          dense
                          color="primary"
                          :class="`fa-light fa-${getBatteryIcon(currentPositionBattery)} fleet-info-icon`"
                          style="line-height: 1.5" />
                        <template #tooltip>{{ $t('dealer-bike.detail.fleet-location.label-battery') }}</template>
                      </label-tooltip>
                      <span>{{ formattedBattery }}</span>
                    </div>
                  </div>
                </GmapInfoWindow>
              </GmapMap>
              <v-alert
                v-else
                class="justify-center font-weight-bold"
                type="warning"
                icon="fa-solid fa-triangle-exclamation"
                text>
                {{ computedErrorMessage }}
              </v-alert>
            </m-form-row>
            <m-form-row class="p-0 google-map-wrapper" v-else>
              <div class="fleet-loading-box">
                <v-progress-circular indeterminate color="primary" />
              </div>
            </m-form-row>
          </template>
        </expansion-panel>

        <expansion-panel
          :title="$t('dealer-bike.detail.observations-title')"
          :readonly="false"
          :hideDivider="false"
          data-panel="damage-log">
          <template v-slot:panel-content>
            <m-form-row>
              <m-note-log
                v-if="dealerBikeSelected"
                v-model="damageLog"
                :add-button-text="$t('dealer-bike.detail.observations-btn-add')"
                :allow-remove="dealerBikeSelected.status !== DealerBikeStatus.Retired"
                :cancel-text="$t('buttons.cancel')"
                :loading="damageLogLoading"
                :placeholder="$t('dealer-bike.detail.observations-placeholder')"
                :readonly="dealerBikeSelected.status === DealerBikeStatus.Retired"
                :remove-dialog-text="$t('dealer-bike.detail.observations-remove-dialog-text')"
                :remove-dialog-title="$t('dealer-bike.detail.observations-remove-dialog-title')"
                remove-requires-confirmation />
            </m-form-row>
          </template>
        </expansion-panel>

        <expansion-panel
          :title="$t('dealer-bike.detail.history-loans-title')"
          :readonly="false"
          data-panel="loans-history">
          <template v-slot:panel-content>
            <finished-loans-table :dealerBikeId="dealerBikeId" />
          </template>
        </expansion-panel>
      </v-expansion-panels>

      <v-card-actions class="px-6 pt-4 pb-10">
        <div class="my-spacer"></div>
        <v-btn class="btn-primary" @click.once="goToDealerBikesList">
          {{ $t('buttons.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref, watch} from '@vue/composition-api'
import {DateTime} from 'luxon'

import coreApi from '@/api/core.api.js'
import FinishedLoansTable from '@/components/dealer-bike/sections/FinishedLoansTable.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import LabelTooltip from '@/components/text/LabelTooltip.vue'
import {useWebSocket} from '@/composables/websocket'
import {GoogleMapsPosition, MapitDeviceStatus, WebSocketDeviceState} from '@/types'
import {DealerBikeStatus} from '@/types/dealer-bikes'
import {Subscription, SubscriptionStatus} from '@/types/dynamodb-types'
import {toDateFormat} from '@/utils/date-helper'
import {mapGetters} from '@/utils/map-store'

import Imei from '../general/Imei.vue'

export default defineComponent({
  name: 'DealerBikeDetail',
  components: {
    LabelTooltip,
    FinishedLoansTable,
    ExpansionPanel,
    Imei,
  },
  setup(props, {root, refs}) {
    const {
      getIdToken: token,
      isLoadingDealerBikes: loading,
      getDealerBikeSelected: dealerBikeSelected,
      getVehicleTotalKm: vehicleTotalKm,
      getLocale: locale,
      getPermissions: permissions,
    } = mapGetters(root.$store.getters)

    const subscription = ref<Subscription>()
    const panels = ref([0, 1, 2, 3, 4])
    const damageLogLoading = ref(false)
    const dealerBikeLoading = ref(true)

    const currentPositionStatus = ref('')
    const currentPositionDate = ref('')
    const currentPositionBattery = ref(0)
    const lastPositionStatus = ref('')
    const lastPositionBattery = ref(0)

    const formattedBattery = computed(() => {
      return `${currentPositionBattery.value}%`
    })

    const currentMapCenter = ref<GoogleMapsPosition | null>(null)
    const lastMapCenter = ref<GoogleMapsPosition | null>(null)

    const dealerBikeId = computed(() => {
      return root.$route.params.dealerBikeId
    })

    const damageLog = computed({
      get: () => {
        return dealerBikeSelected.value?.damageLog
      },
      set: newVal => {
        damageLogLoading.value = true
        root.$store.dispatch('updateDealerBike', {damageLog: newVal}).finally(() => (damageLogLoading.value = false))
      },
    })

    const computedErrorMessage = computed(() => {
      if (dealerBikeSelected.value && dealerBikeSelected.value.status === DealerBikeStatus.Retired)
        return root.$t('dealer-bike.detail.fleet-location.retired')
      if (dealerBikeSelected.value && dealerBikeSelected.value.status === DealerBikeStatus.Unavailable)
        return root.$t('dealer-bike.detail.fleet-location.unavailable')
      if (!subscription.value || subscription.value.status === SubscriptionStatus.Cancelled) {
        return root.$t('dealer-bike.detail.fleet-location.no-subscription')
      }
      if (!currentMapCenter.value) return root.$t('dealer-bike.detail.fleet-location.no-data')

      return root.$t('dealer-bike.detail.fleet-location.unknown-error')
    })

    const {init: initWebsocket, loading: loadingWebsocket} = useWebSocket<WebSocketDeviceState>()

    watch(dealerBikeSelected, newVal => {
      if (newVal && newVal.device.id && permissions.value.fleetManagement.canSee && token.value) {
        initWebsocket({
          url: `${process.env.VUE_APP_MAPIT_WS_URL}/devicestate/${newVal.device.id}`,
          token: token.value,
          firstMessageTimeout: 10000,
          onMessage: async message => {
            lastMapCenter.value = {lat: message.lat, lng: message.lng}
            currentMapCenter.value =
              lastMapCenter.value.lat && lastMapCenter.value.lng ? lastMapCenter.value : currentMapCenter.value

            const lastTs = DateTime.fromMillis(message.lastTs || DateTime.now().toMillis())
            currentPositionDate.value = `${lastTs.toLocaleString(DateTime.TIME_24_SIMPLE)} - ${toDateFormat(
              lastTs,
              locale.value,
            )}`

            lastPositionStatus.value = message.status
            lastPositionBattery.value = message.battery

            currentPositionStatus.value = lastPositionStatus.value || currentPositionStatus.value || 'NO_COMMS'
            currentPositionBattery.value = lastPositionBattery.value || currentPositionBattery.value || 0

            currentMapCenter.value && (await centerPosition(currentMapCenter.value))
          },
        })
      }
    })

    watch(loadingWebsocket, async newVal => {
      if (!newVal && currentMapCenter.value) {
        await centerPosition(currentMapCenter.value)
      }
    })

    onMounted(async () => {
      await loadDealerBike()
    })

    async function centerPosition(position: GoogleMapsPosition) {
      currentMapCenter.value = position
      const mapRef = refs.mapRef as any

      if (!mapRef) return

      const map = await mapRef.$mapPromise

      map.panTo(currentMapCenter.value)
      map.panBy(0, -48)
    }

    async function loadDealerBike() {
      try {
        await root.$store.dispatch('loadDealerBike', dealerBikeId.value)
        const vehicleId = dealerBikeSelected.value.vehicle.id

        const {data: vehicle} = await coreApi.getVehicle(vehicleId)
        root.$store.dispatch('setVehicleKms', vehicle.km || 0)
        subscription.value = vehicle.subscription
      } catch (e: any) {
        const errorKey = 'dealer-bike.detail.error.default'
        console.error(root.$t(errorKey), {cause: e})

        if (e.response?.status === 404) {
          return // not display the error if the motorbike is retired (not found)
        }

        root.$store.dispatch('setAlertResult', {
          type: 'error',
          stringId: errorKey,
          open: true,
        })
      } finally {
        dealerBikeLoading.value = false
      }
    }

    function goToDealerBikesList() {
      root.$router.push({name: 'DealerBikesList'})
    }

    function getDataFormatted(data: any) {
      return toDateFormat(data, locale.value)
    }

    function hasActiveSubscription() {
      return subscription.value && subscription.value.status === SubscriptionStatus.Active
    }

    function isAvailable() {
      return dealerBikeSelected.value && dealerBikeSelected.value.status === DealerBikeStatus.Available
    }

    function getDeviceStatus(status: MapitDeviceStatus | string) {
      switch (status) {
        case 'AT_REST':
          return root.$t('dealer-bike.detail.fleet-location.at-rest')
        case 'MOVING':
          return root.$t('dealer-bike.detail.fleet-location.moving')
        case 'BUZZ':
          return root.$t('dealer-bike.detail.fleet-location.buzz')
        case 'NO_COMMS':
          return root.$t('dealer-bike.detail.fleet-location.no-comms')
      }
    }

    function getBatteryIcon(battery: number) {
      if (battery >= 80) return 'battery-full'
      if (battery >= 60) return 'battery-three-quarters'
      if (battery >= 40) return 'battery-half'
      if (battery >= 20) return 'battery-quarter'
      if (battery > 0) return 'battery-low'
      return 'battery-slash'
    }

    return {
      centerPosition,
      computedErrorMessage,
      currentMapCenter: currentMapCenter,
      currentPositionBattery: currentPositionBattery,
      currentPositionDate: currentPositionDate,
      currentPositionStatus,
      damageLog,
      damageLogLoading,
      dealerBikeId,
      dealerBikeLoading,
      dealerBikeSelected,
      DealerBikeStatus,
      formattedBattery,
      getBatteryIcon,
      getDataFormatted,
      getDeviceStatus,
      goToDealerBikesList,
      hasActiveSubscription,
      isAvailable,
      loadDealerBike,
      loading,
      loadingWebsocket,
      panels,
      permissions,
      subscription,
      vehicleTotalKm,
    }
  },
})
</script>

<style lang="scss">
.dealer-bike-detail {
  .google-map-wrapper {
    .v-skeleton-loader__image.v-skeleton-loader__bone {
      width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
      height: 300px;
    }
  }

  .gm-style-iw-c {
    padding: 0 !important;
    height: 52px !important;
    overflow: visible !important;
    border-radius: 8px !important;
    box-shadow: none !important;
    background: transparent !important;

    > button {
      display: none !important;
    }
  }

  .gm-style-iw-d {
    padding: 0 !important;
    overflow: unset !important;
    max-height: unset !important;
    > div {
      width: 100%;
      height: 52px;
    }
  }

  .gm-style-iw-chr,
  .gm-style-iw-tc {
    display: none !important;
  }
}
</style>

<style scoped lang="scss">
@mixin ripple($duration, $delay) {
  opacity: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  content: '';
  height: 100%;
  width: 100%;
  background: #0075eb;
  border-radius: 100%;
  animation-name: ripple;
  animation-duration: $duration;
  animation-delay: $delay;
  animation-iteration-count: infinite;
  animation-timing-function: cubic-bezier(0.65, 0, 0.34, 1);
  z-index: -1;
}

.dealer-bike-detail {
  padding-bottom: 2rem;
}
.break-word {
  word-break: break-word;
}
.google-map-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}
.motorbike-icon-wrapper {
  cursor: pointer;
  position: absolute;
  top: -48px;
  left: 50%;
  transform: translate(-50%, 0);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background-color: #dc182d;
  outline: 3px solid #fff;
  padding: 0.5rem;
  border-radius: 50%;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3)) !important;
  z-index: 2;
}
.motorbike-circle {
  cursor: pointer;
  position: absolute;
  bottom: -36px;
  left: 50%;
  transform: translate(-50%, 0);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #0075eb;
  outline: 2px solid #fff;
  filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.4)) !important;

  &::after {
    @include ripple(3s, 0s);
  }
}
.fleet-info-background {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  border-radius: 8px !important;
  background-color: #fff;
  filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.4)) !important;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translate(-50%, 0) rotate(45deg);
    display: block;
    width: 20px;
    height: 20px;
    background-color: #fff;
    border-bottom-right-radius: 4px;
  }
}

.fleet-info-wrapper {
  cursor: pointer;
  width: 100%;
  height: 100%;
  padding: 0 30px;
  display: grid;
  grid-template-rows: 1fr;
  grid-auto-flow: column;
  gap: 24px;

  font-family: 'ProximaNovaSemibold', sans-serif;
  font-size: 14px;
  color: #222;
}

.fleet-info {
  display: flex;
  align-items: center;
  z-index: 1;

  .fleet-info-icon {
    margin-right: 10px;
  }
}

.fleet-loading-box {
  height: 300px;
  width: 100%;
  backdrop-filter: brightness(90%);
  display: grid;
  place-items: center;
}

@keyframes ripple {
  from {
    opacity: 1;
    transform: scale3d(0.75, 0.75, 1) translateZ(0);
  }

  to {
    opacity: 0;
    transform: scale3d(3, 3, 1) translateZ(0);
  }
}
</style>
